import { useState, type MouseEvent } from 'react';
import { 
  Typography, 
  Box, 
  Grid, 
  Card, 
  CardContent, 
  CardActions, 
  Button, 
  Chip, 
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  List,
  ListItem,
  ListItemText,
  Paper,
  IconButton,
  Tooltip,
  Divider
} from '@mui/material';
import { 
  CalendarMonth as CalendarIcon, 
  Download as DownloadIcon, 
  Link as LinkIcon, 
  RssFeed as SubscribeIcon,
  GridView as GridViewIcon,
  ViewList as ViewListIcon
} from '@mui/icons-material';

interface CalendarEntry {
  id: string;
  title: string;
  description: string;
  icsPath: string;
  webcalUrl: string;
}

const calendars: CalendarEntry[] = [
  {
    id: '1',
    title: 'Executive and Governing Boards',
    description: 'Comprehensive schedule for executive leadership meetings and primary governing board sessions.',
    icsPath: '/calendars/executive-and-governing-boards.ics',
    webcalUrl: 'webcal://matocs.org/calendars/executive-and-governing-boards.ics',
  },
  {
    id: '2',
    title: 'Local Transit Agency Boards',
    description: 'Meeting schedules for regional and local transit agencies, including community transit partners.',
    icsPath: '/calendars/local-transit-agency-boards.ics',
    webcalUrl: 'webcal://matocs.org/calendars/local-transit-agency-boards.ics',
  },
  {
    id: '3',
    title: 'Policy and Planning',
    description: 'Strategic planning sessions, policy review committees, and long-range development forums.',
    icsPath: '/calendars/policy-and-planning.ics',
    webcalUrl: 'webcal://matocs.org/calendars/policy-and-planning.ics',
  },
  {
    id: '4',
    title: 'PSRC Governance',
    description: 'Puget Sound Regional Council (PSRC) governance meetings, regional planning, and committee updates.',
    icsPath: '/calendars/psrc-governance.ics',
    webcalUrl: 'webcal://matocs.org/calendars/psrc-governance.ics',
  },
  {
    id: '5',
    title: 'Sound Transit Governance',
    description: 'Dedicated board and committee meeting schedule for Sound Transit regional transit authority.',
    icsPath: '/calendars/sound-transit-governance.ics',
    webcalUrl: 'webcal://matocs.org/calendars/sound-transit-governance.ics',
  },
  {
    id: '6',
    title: 'State Transportation Governance',
    description: 'Washington State Department of Transportation (WSDOT) and state-level legislative hearing schedules.',
    icsPath: '/calendars/state-transportation-governance.ics',
    webcalUrl: 'webcal://matocs.org/calendars/state-transportation-governance.ics',
  },
  {
    id: '7',
    title: 'Western WA Transport Master',
    description: 'Master combined calendar for all Western Washington transportation governance and regional planning.',
    icsPath: '/calendars/western-wa-transport-governance-master.ics',
    webcalUrl: 'webcal://matocs.org/calendars/western-wa-transport-governance-master.ics',
  },
];

const Calendars = () => {
  const [view, setView] = useState<'grid' | 'list'>('grid');

  const handleViewChange = (
    _event: MouseEvent<HTMLElement>,
    nextView: 'grid' | 'list' | null,
  ) => {
    if (nextView !== null) {
      setView(nextView);
    }
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        <Box>
          <Typography variant="h4" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
            Project Calendars
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, maxWidth: 800 }}>
            Access the full suite of transportation governance and strategic planning calendars. 
            Subscribe to stay synchronized with regional transit authorities and board milestones.
          </Typography>
        </Box>
        <ToggleButtonGroup
          value={view}
          exclusive
          onChange={handleViewChange}
          aria-label="view toggle"
          size="small"
          color="primary"
          sx={{ mt: 1 }}
        >
          <ToggleButton value="grid" aria-label="grid view">
            <Tooltip title="Grid View">
              <GridViewIcon />
            </Tooltip>
          </ToggleButton>
          <ToggleButton value="list" aria-label="list view">
            <Tooltip title="List View">
              <ViewListIcon />
            </Tooltip>
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {view === 'grid' ? (
        <Grid container spacing={3}>
          {calendars.map((calendar) => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={calendar.id}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', transition: '0.3s', '&:hover': { transform: 'translateY(-5px)', boxShadow: 4 } }}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <CalendarIcon color="primary" sx={{ mr: 1 }} />
                    <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
                      {calendar.title}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {calendar.description}
                  </Typography>
                  
                  <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                    <Chip 
                      icon={<SubscribeIcon sx={{ fontSize: '1rem !important' }} />} 
                      label="Subscribe via webcal" 
                      size="small" 
                      color="secondary" 
                      variant="outlined"
                      component="a"
                      href={calendar.webcalUrl}
                      clickable
                    />
                  </Stack>
                </CardContent>
                <CardActions sx={{ p: 2, pt: 0, justifyContent: 'space-between' }}>
                  <Button 
                    size="small" 
                    startIcon={<LinkIcon />} 
                    href={calendar.icsPath} 
                    target="_blank"
                  >
                    View File
                  </Button>
                  <Button 
                    size="small" 
                    variant="contained" 
                    startIcon={<DownloadIcon />} 
                    href={calendar.icsPath}
                    download
                  >
                    Download .ics
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        <Paper elevation={2}>
          <List disablePadding>
            {calendars.map((calendar, index) => (
              <Box key={calendar.id}>
                <ListItem
                  sx={{
                    py: 2,
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    gap: 2
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
                    <CalendarIcon color="primary" sx={{ mr: 2, display: { xs: 'none', sm: 'block' } }} />
                    <ListItemText
                      primary={
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                          {calendar.title}
                        </Typography>
                      }
                      secondary={calendar.description}
                    />
                  </Box>
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems="center" sx={{ width: { xs: '100%', sm: 'auto' } }}>
                    <Chip 
                      icon={<SubscribeIcon sx={{ fontSize: '1rem !important' }} />} 
                      label="Subscribe via webcal" 
                      size="small" 
                      color="secondary" 
                      variant="outlined"
                      component="a"
                      href={calendar.webcalUrl}
                      clickable
                      sx={{ mr: { md: 2 }, mb: { xs: 1, md: 0 }, width: { xs: '100%', sm: 'auto' } }}
                    />
                    <Box sx={{ display: 'flex', gap: 1, width: { xs: '100%', sm: 'auto' }, justifyContent: 'flex-end' }}>
                      <Tooltip title="View File">
                        <IconButton href={calendar.icsPath} target="_blank" color="primary" size="small">
                          <LinkIcon />
                        </IconButton>
                      </Tooltip>
                      <Button 
                        size="small" 
                        variant="contained" 
                        startIcon={<DownloadIcon />} 
                        href={calendar.icsPath}
                        download
                        sx={{ whiteSpace: 'nowrap' }}
                      >
                        Download .ics
                      </Button>
                    </Box>
                  </Stack>
                </ListItem>
                {index < calendars.length - 1 && <Divider />}
              </Box>
            ))}
          </List>
        </Paper>
      )}
    </Box>
  );
};

export default Calendars;
